/* global BigInt */
// BigInt is ES2020; CRA's "react-app" ESLint env only declares ES2015 globals, so it
// has to be declared here for the dev-server lint pass to accept it.
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { NUSDIcon } from "../../../AssetComponents/Images";
import { toast } from "react-toastify";
import { PurpleButton } from "../../../Components/Buttons/PurpleButton";
import { WhiteButton } from "../../../Components/Buttons/WhiteButton";
import WithdrawalTimeline from "../../../Components/VaultsComponents/WithdrawalTimeline";
import SegmentedTabs from "../../../Components/Selection/SegmentedTabs";
import { Skeleton, SkeletonLine } from "../../../Components/utils/Skeleton";
import { 
  getUserVaultDeposits, 
  getUserLockedDeposits,
  getUserWithdrawRequests,
  makeWithdrawRequest,
  cancelWithdrawRequest,
  unlockDeposit,
  redeemShares
} from "./VaultQueries";
// Add EVM imports
import {
  makeWithdrawRequestEvm,
  cancelWithdrawRequestEvm,
  unlockDepositEvm,
  redeemSharesEvm,
  getVaultShareErc20Address,
  getVaultShareBalanceBaseUnitsEvm
} from "../../../evm/services/vaultsEvmService";
import { evmToBech32Address } from "../../../evm/services/EvmContract";
import { queryBalance } from "../../../Components/functions/Contract";
import { getNibiruSDK, getContractAddresses } from "../../../config/network";
import { useWalletConnection } from "../../../hooks/useWalletConnection";
import { showTransactionToast, dismissTransactionToast } from "../../../Components/Toast/TransactionToast";

/* ---------------------------------------------------------------------------
   PREVIEW DATA  —  `/perps/vaults/deposits?mock=1`
   ---------------------------------------------------------------------------
   Every state on this page is behind a funded wallet, which makes the populated
   table the one layout nobody can look at while building it. `?mock=1` fills
   the three tabs with fixtures and skips the chain entirely; nothing reads the
   flag unless it is in the URL, so this cannot leak into a real session.

   The figures are deliberately awkward — long share counts, a four-figure
   value, a locked row next to an unlockable one — because that is what the
   column widths and the `ce-figure` tabular alignment actually have to hold. */
const MOCK_DEPOSITS = [
  { vaultName: "USDC Vault", vaultTokens: "12480.5521", vaultTokenName: "SLP-USDC", currentValue: "13102.44", sharePrice: "1.0498" },
  { vaultName: "USDC Vault", vaultTokens: "842.0100", vaultTokenName: "SLP-USDC", currentValue: "884.05", sharePrice: "1.0498" },
];

const MOCK_LOCKED = [
  { depositId: 1, vaultId: "usdc", vaultName: "USDC Vault", depositDate: "12 Jun 2026", depositAmount: "5000.00", vaultTokens: "4762.1904", vaultTokenName: "SLP-USDC", unlockDate: "12 Sep 2026", isLocked: true },
  { depositId: 2, vaultId: "usdc", vaultName: "USDC Vault", depositDate: "03 Mar 2026", depositAmount: "1250.00", vaultTokens: "1190.4761", vaultTokenName: "SLP-USDC", unlockDate: "03 Jun 2026", isLocked: false },
];

const MOCK_WITHDRAWALS = [
  { shares: "2400.0000", unlockEpoch: 148, canWithdraw: true },
  { shares: "615.5000", unlockEpoch: 151, canWithdraw: false },
];

const MyDeposits = () => {
  const [deposits, setDeposits] = useState([]);
  const [lockedDeposits, setLockedDeposits] = useState([]);
  const [withdrawRequests, setWithdrawRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('deposits');
  
  const account = useSelector((state) => state.auth.account);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMock = searchParams.get("mock") === "1";
  
  // Add wallet connection hook
  const { isEvm, isCosmos } = useWalletConnection();
  
  useEffect(() => {
    if (isMock) {
      setDeposits(MOCK_DEPOSITS);
      setLockedDeposits(MOCK_LOCKED);
      setWithdrawRequests(MOCK_WITHDRAWALS);
      setLoading(false);
      return;
    }
    fetchUserData();
    // isEvm is derived from redux and can resolve after the first render; without it
    // in the deps the deposits are fetched once down the wrong (Cosmos) path and
    // never refetched, so an EVM wallet shows nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, isEvm, isMock]);
  
  const fetchUserData = async () => {
    if (!account) {
      setDeposits([]);
      setLockedDeposits([]);
      setWithdrawRequests([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    try {
      // Fetch all data in parallel, passing EVM flag
      const [depositsData, lockedDepositsData, withdrawRequestsData] = await Promise.all([
        getUserVaultDeposits(account, isEvm),
        getUserLockedDeposits(account, isEvm),
        getUserWithdrawRequests(account, isEvm)
      ]);
      
      setDeposits(depositsData);
      setLockedDeposits(lockedDepositsData);
      setWithdrawRequests(withdrawRequestsData);
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast.error("Failed to load your vault data");
    } finally {
      setLoading(false);
    }
  };
  
  // Updated handleMakeWithdrawRequest function to show transaction modal
  const handleMakeWithdrawRequest = async () => {
    if (!account) {
      toast.error("Please connect your wallet");
      return;
    }
    
    try {
      showTransactionToast("Processing withdrawal, please wait...");

      let result = false;

      if (isEvm) {
        // Use EVM integration
        console.log("Using EVM integration for withdrawal request");

        // Get the vault address
        const vaultAddress = getContractAddresses().vault;

        // Check if user has active deposits
        if (deposits.length === 0) {
          dismissTransactionToast();
          toast.error("No active deposits found to withdraw from");
          return;
        }

        // Use the exact on-chain base-unit amounts. Formatting to 2 decimals first and
        // re-scaling loses precision and can request more shares than the user holds.
        const shareErc20 = await getVaultShareErc20Address(vaultAddress);
        const erc20Base = await getVaultShareBalanceBaseUnitsEvm(account, shareErc20);

        // Shares that were never bridged to EVM remain in bank under the bech32 address.
        let bankBase = "0";
        try {
          const bech32Address = await evmToBech32Address(account);
          const shareDenom = deposits[0]?.denom;
          if (shareDenom) {
            const bankBalance = await queryBalance(
              bech32Address,
              shareDenom,
              getNibiruSDK().endptTm
            );
            bankBase = bankBalance?.amount || "0";
          }
        } catch (bankError) {
          console.warn("Could not read vault share bank balance:", bankError?.message);
        }

        if (BigInt(erc20Base) + BigInt(bankBase) <= 0n) {
          dismissTransactionToast();
          toast.error("No vault tokens available to withdraw");
          return;
        }

        console.log("Withdrawing vault shares (base units):", { erc20Base, bankBase });

        const evmResult = await makeWithdrawRequestEvm({
          vaultAddress: vaultAddress,
          withdrawAmount: bankBase,
          useErc20Amount: erc20Base
        });

        result = evmResult.success;
      } else if (isCosmos) {
        // Use existing Cosmos integration
        console.log("Using Cosmos integration for withdrawal request");
        result = await makeWithdrawRequest(account, dispatch);
      } else {
        dismissTransactionToast();
        toast.error("Please connect a supported wallet");
        return;
      }

      dismissTransactionToast();
      if (result) {
        toast.success("Withdrawal request submitted!");
        await fetchUserData();
      }
    } catch (error) {
      console.error("Error making withdrawal request:", error);
      dismissTransactionToast();
      toast.error(`Failed to make withdrawal request: ${error.message}`);
    }
  };
  
  // Updated handleCancelWithdrawRequest to support both Cosmos and EVM
  const handleCancelWithdrawRequest = async (unlockEpoch) => {
    if (!account) return;
    
    try {
      let result = false;
      
      if (isEvm) {
        console.log("Using EVM integration for cancel withdrawal request");
        
        const evmResult = await cancelWithdrawRequestEvm({
          unlockEpoch: unlockEpoch
        });
        result = evmResult.success;
      } else if (isCosmos) {
        // Use existing Cosmos integration
        console.log("Using Cosmos integration for cancel withdrawal request");
        result = await cancelWithdrawRequest(account, unlockEpoch, dispatch);
      } else {
        toast.error("Please connect a supported wallet");
        return;
      }
      
      if (result) {
        fetchUserData();
      }
    } catch (error) {
      console.error("Error cancelling withdrawal request:", error);
    }
  };
  
  // Updated handleUnlockDeposit to support both Cosmos and EVM
  const handleUnlockDeposit = async (depositId) => {
    if (!account) return;
    
    try {
      let result = false;
      
      if (isEvm) {
        // Function not available in EVM interface
        toast.warning("Unlock deposit is not available in EVM mode yet. Please use Cosmos wallet for this function.");
        return;
      } else if (isCosmos) {
        // Use existing Cosmos integration
        console.log("Using Cosmos integration for unlock deposit");
        result = await unlockDeposit(account, depositId, dispatch);
      } else {
        toast.error("Please connect a supported wallet");
        return;
      }
      
      if (result) {
        fetchUserData();
      }
    } catch (error) {
      console.error("Error unlocking deposit:", error);
    }
  };
  
  // Updated handleRedeemShares to support both Cosmos and EVM
  const handleRedeemShares = async (shares) => {
    if (!account) return;
    
    try {
      let result = false;
      
      if (isEvm) {
        // Use EVM integration
        console.log("Using EVM integration for redeem shares");
        
        // Convert shares to proper amount format
        const redeemAmount = Math.floor(
          parseFloat(shares) * (10 ** process.env.REACT_APP_USDC_DECIMALS)
        ).toString();
        
        const evmResult = await redeemSharesEvm({
          vaultAddress: getContractAddresses().vault,
          redeemAmount: redeemAmount,
          sendCollateralToEvm: true,
          receiver: null,
          depositor: account
        });
        result = evmResult.success;
      } else if (isCosmos) {
        // Use existing Cosmos integration
        console.log("Using Cosmos integration for redeem shares");
        result = await redeemShares(account, shares, dispatch);
      } else {
        toast.error("Please connect a supported wallet");
        return;
      }
      
      if (result) {
        fetchUserData();
      }
    } catch (error) {
      console.error("Error redeeming shares:", error);
    }
  };
  
  if (!account && !isMock) {
    return (
      <div className="ce-vault-card w-full max-w-full overflow-x-hidden p-4 md:p-6" style={{ fontFamily: "General Sans" }}>
        <div className="ce-vault-empty">
          <h2 className="text-lg font-semibold mb-1" style={{ color: "var(--ink)" }}>Connect Wallet</h2>
          <p style={{ color: "var(--ink-3)", fontSize: 13 }}>Connect your wallet to view your vault deposits</p>
        </div>
      </div>
    );
  }
  
  const totals = {
    value: deposits.reduce((sum, d) => sum + parseFloat(d.currentValue), 0).toFixed(2),
    shares: deposits.reduce((sum, d) => sum + parseFloat(d.vaultTokens), 0).toFixed(2),
    sharePrice: deposits[0]?.sharePrice,
  };

  return (
    <div className="ce-vault-card w-full max-w-full overflow-x-hidden p-4 md:p-6" style={{ fontFamily: "General Sans" }}>
      <div className="mb-5">
        <h1 className="text-xl md:text-2xl font-semibold" style={{ color: "var(--ink)" }}>My Deposits</h1>
        <p style={{ color: "var(--ink-3)", fontSize: 13 }}>View and manage your vault deposits</p>
      </div>

      {/* Summary niches — recessed, like every other readout on this page. The
          three were a lavender box next to two grey ones, which said nothing
          about the difference between them. */}
      {loading && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="ce-vault-well p-4">
              <SkeletonLine w={76} h={10} index={i} />
              <SkeletonLine w={104} h={18} index={i} style={{ marginTop: 10 }} />
            </div>
          ))}
        </div>
      )}

      {!loading && deposits.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="ce-vault-well p-4">
            <div className="ce-eyebrow mb-1">Total Value</div>
            <div className="ce-figure" style={{ fontWeight: 600, fontSize: 18, color: "var(--ink)" }}>${totals.value}</div>
          </div>
          <div className="ce-vault-well p-4">
            <div className="ce-eyebrow mb-1">Shares Held</div>
            <div className="ce-figure" style={{ fontWeight: 600, fontSize: 18, color: "var(--cat-accent, #5B1DEE)" }}>
              {totals.shares} SLP-USDC
            </div>
          </div>
          <div className="ce-vault-well p-4">
            <div className="ce-eyebrow mb-1">Share Price</div>
            <div className="ce-figure" style={{ fontWeight: 600, fontSize: 18, color: "var(--ink)" }}>
              {totals.sharePrice ? `$${totals.sharePrice}` : "—"}
            </div>
          </div>
        </div>
      )}
      
      {/* Tabs. Underline tabs on a hairline rule were the one switch shape the
          app no longer uses anywhere else. */}
      <div className="mb-6">
        <SegmentedTabs
          label="Deposits view"
          value={activeTab}
          onChange={setActiveTab}
          items={[
            { value: "deposits", label: "Active Deposits" },
            {
              value: "withdrawals",
              label: "Withdrawal Requests",
              badge: withdrawRequests.length > 0
                ? <span className="ce-chip ce-chip-accent" style={{ marginLeft: 6 }}>{withdrawRequests.length}</span>
                : null,
            },
            {
              value: "locked",
              label: "Locked Deposits",
              disabled: true,
              badge: <span className="ce-chip ce-chip-accent" style={{ marginLeft: 6 }}>Soon</span>,
            },
          ]}
        />
      </div>

      {activeTab === 'withdrawals' && (
        <div className="mb-6">
          {/* <h2 className="text-[#202020] text-lg font-medium mb-4">No Withdrawal Requests</h2>
          <p className="text-[#5A5A5A] mb-6">You don't have any pending withdrawal requests</p> */}
          <WithdrawalTimeline />
        </div>
      )}
      
      {loading ? (
        // The deposits table, pending — four columns at the row height the
        // real table uses, so the rows land in place instead of the page
        // jumping when the vault query answers.
        <div className="w-full">
          <div className="grid grid-cols-4 gap-3 items-center px-4" style={{ height: 34 }}>
            {[64, 56, 88, 72].map((w, i) => (
              <SkeletonLine key={i} w={w} h={10} index={i} />
            ))}
          </div>
          {[0, 1, 2].map((row) => (
            <div key={row} className="grid grid-cols-4 gap-3 items-center px-4" style={{ height: 62 }}>
              <div className="flex items-center gap-2">
                <Skeleton w={20} h={20} radius="999px" index={row} />
                <SkeletonLine w={84} h={14} index={row} />
              </div>
              <SkeletonLine w={110} h={14} index={row} />
              <SkeletonLine w={78} h={14} index={row} />
              <SkeletonLine w={148} h={34} index={row} />
            </div>
          ))}
        </div>
      ) : activeTab === 'deposits' && deposits.length === 0 ? (
        <div className="ce-vault-empty">
          <h2 className="text-lg font-semibold mb-1" style={{ color: "var(--ink)" }}>No Deposits Found</h2>
          <p className="mb-4" style={{ color: "var(--ink-3)", fontSize: 13 }}>You don't have any active vault deposits</p>
          <PurpleButton
            text="Explore Vaults"
            onClick={() => navigate('/perps/vaults')}
          />
        </div>
      ) : activeTab === 'locked' && lockedDeposits.length === 0 ? (
        <div className="ce-vault-empty">
          <h2 className="text-lg font-semibold mb-1" style={{ color: "var(--ink)" }}>No Locked Deposits</h2>
          <p style={{ color: "var(--ink-3)", fontSize: 13 }}>You don't have any locked vault deposits</p>
        </div>
      ) : activeTab === 'withdrawals' && withdrawRequests.length === 0 ? (
        <div className="ce-vault-empty">
          <h2 className="text-lg font-semibold mb-1" style={{ color: "var(--ink)" }}>No Withdrawal Requests</h2>
          <p style={{ color: "var(--ink-3)", fontSize: 13 }}>You don't have any pending withdrawal requests</p>
        </div>
      ) : (
        <>
          {/* Active Deposits Table */}
          {activeTab === 'deposits' && (
            <div className="w-full overflow-x-auto">
              <table className="ce-vault-table min-w-full">
                <thead>
                  <tr>
                    <th>Vault</th>
                    <th>Shares</th>
                    <th>Current Value</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deposits.map((deposit, index) => (
                    <tr key={index}>
                      <td>
                        <div className="flex items-center">
                          <NUSDIcon className="w-5 h-5 mr-2" />
                          <div className="font-medium">{deposit.vaultName}</div>
                        </div>
                      </td>
                      <td>
                        <span className="ce-figure font-semibold" style={{ color: "var(--cat-accent, #5B1DEE)", fontSize: 14 }}>
                          {deposit.vaultTokens} {deposit.vaultTokenName}
                        </span>
                      </td>
                      <td>
                        <span className="ce-figure font-semibold" style={{ color: "var(--up)", fontSize: 14 }}>
                          ${deposit.currentValue}
                        </span>
                      </td>
                      <td>
                        <div className="inline-block">
                          <PurpleButton
                            text="Request Withdraw"
                            onClick={handleMakeWithdrawRequest}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Locked Deposits Table */}
          {activeTab === 'locked' && (
            <div className="w-full overflow-x-auto">
              <table className="ce-vault-table min-w-full">
                <thead>
                  <tr>
                    <th>Vault</th>
                    <th>Deposit Date</th>
                    <th>Deposited</th>
                    <th>Shares</th>
                    <th>Unlock Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lockedDeposits.map((deposit, index) => (
                    <tr key={index}>
                      <td>
                        <div className="flex items-center">
                          <NUSDIcon className="w-5 h-5 mr-2" />
                          <div className="font-medium">{deposit.vaultName}</div>
                        </div>
                      </td>
                      <td>
                        {deposit.depositDate}
                      </td>
                      <td>
                        ${deposit.depositAmount} USDC
                      </td>
                      <td>
                        <span
                          className="ce-figure font-semibold"
                          style={{ color: "var(--cat-accent, #5B1DEE)", fontSize: 14 }}
                        >
                          {deposit.vaultTokens} {deposit.vaultTokenName}
                        </span>
                      </td>
                      <td>
                        {deposit.unlockDate}
                      </td>
                      <td>
                        {deposit.isLocked ? (
                          <span className="ce-chip" style={{ background: "var(--tint-warn)", color: "var(--on-tint-warn)" }}>
                            Locked
                          </span>
                        ) : (
                          <span className="ce-chip ce-chip-up">
                            Unlockable
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex space-x-3">
                          {!deposit.isLocked && (
                            <PurpleButton 
                              text="Unlock"
                              onClick={() => handleUnlockDeposit(deposit.depositId)}
                            />
                          )}
                          <WhiteButton 
                            text="View"
                            onClick={() => navigate(`/perps/vaults/${deposit.vaultId}`)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Withdrawal Requests Table */}
          {activeTab === 'withdrawals' && (
            <div className="w-full overflow-x-auto">
              <table className="ce-vault-table min-w-full">
                <thead>
                  <tr>
                    <th>Withdraw Amount</th>
                    {/* <th>Request Date</th> */}
                    <th>Unlock Epoch</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawRequests.map((request, index) => (
                    <tr key={index}>
                      <td>
                        {request.shares} USDC
                      </td>
                      {/* <td>
                        {request.requestDate}
                      </td> */}
                      <td>
                        {request.unlockEpoch}
                      </td>
                      <td>
                        {request.canWithdraw ? (
                          <div className="flex items-center">
                            <span className="ce-chip ce-chip-up">
                              Ready to Withdraw
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <span className="ce-chip" style={{ background: "var(--tint-warn)", color: "var(--on-tint-warn)" }}>
                              Pending
                            </span>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="flex space-x-3">
                          {request.canWithdraw ? (
                            <>
                              <PurpleButton 
                                text="Redeem now!"
                                onClick={() => handleRedeemShares(request.shares)}
                              />
                              <WhiteButton 
                                text="Cancel"
                                onClick={() => handleCancelWithdrawRequest(request.unlockEpoch)}
                              />
                            </>
                          ) : (
                            <WhiteButton 
                              text="Cancel"
                              onClick={() => handleCancelWithdrawRequest(request.unlockEpoch)}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      
    </div>
  );
};

export default MyDeposits;